import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { combineAll, DcBaseComponent } from '@datachain/ui-sdk/common';
import { ModalData } from '@datachain/ui-sdk/components/app-modal';
import { PanelAction } from '@datachain/ui-sdk/components/app-panel';
import { AppIcons } from '@datachain/ui-sdk/components/dc-icon';
import { Store } from '@ngrx/store';
import { DxDataGridComponent } from 'devextreme-angular';
import {
	BehaviorSubject,
	debounceTime,
	firstValueFrom,
	Observable,
	of,
	ReplaySubject,
	takeUntil,
	tap,
	timer,
} from 'rxjs';

import { DxListsStorage, IDxList } from '../../../core/dx-lists.storage';
import {
	checkStringEquality,
	DcDxGrids,
	DxFilterElem,
	DxHeaderFilterItem,
	DxOperations,
	HeaderFilterDefaultMapper,
	NoopValue,
} from '../../../core/dx-types';
import { ProjectStorage } from '../../../core/project.storage';
import {
	clearCacheJobs,
	initTaskList,
	killAllJobs,
	killJob,
	refreshTaskList,
	TaskSelector,
	toggleJobsCacheActive,
	updateViewActiveJobs,
} from '../../../core/store';
import { ActionIcons } from '../../app-actions.icons';
import { ComponentIcons } from '../../app-components.icons';
import { DcIcons } from '../../app-dc.icons';
import { GdIcons } from '../../app-gd.icons';
import { DcLoaders } from '../../app-loaders';
import { ActionIconsInjectable } from '../../ui.module';
import { TaskItemEntity } from './app-task-item.entity';
import { TaskListEntity } from './app-task-list.entity';

type TaskOverlayErrorMessage = {
	errorMessageTaskId: number | string | undefined;
};
@Component({
	selector: 'app-task-modal',
	templateUrl: 'app-task-modal.component.html',
	styleUrls: ['./app-task-modal.component.scss'],
})
export class AppTaskModalComponent extends DcBaseComponent implements OnInit {
	protected readonly DcLoaders = DcLoaders;
	protected readonly DcIcons = DcIcons;
	protected readonly GdIcons = GdIcons;
	protected readonly ComponentIcons = ComponentIcons;
	public ActionIcons = ActionIcons;
	public DxOperations = DxOperations;

	public panelActions: Array<PanelAction> = [];
	public taskTypeHeaderFilter: Array<DxHeaderFilterItem> = [];
	public projectHeaderFilter: Array<DxHeaderFilterItem> = [];

	public vo$!: Observable<{
		isFirstLoad: boolean;
		isLoading: boolean;
		list: TaskListEntity;
		viewActiveJobs: boolean;
		isJobsCacheActive: boolean;
		openedOverlays: TaskOverlayErrorMessage;
	}>;

	public loadState = (): IDxList | undefined =>
		this.dxStorage.getListState(this.dxLocalStorageKey);

	public saveState = (state: IDxList): void => {
		this.dxStorage.setListState(this.dxLocalStorageKey, {
			...state,
			searchText: '',
		});
	};

	@ViewChild(DxDataGridComponent)
	private readonly grid: DxDataGridComponent | null = null;
	private readonly openedOverlaySubject =
		new BehaviorSubject<TaskOverlayErrorMessage>({
			errorMessageTaskId: undefined,
		});
	private readonly taskListSubject = new ReplaySubject<TaskListEntity>(1);

	private readonly firstLoadSubject = new BehaviorSubject<boolean>(true);

	public constructor(
		private readonly store: Store,
		private readonly taskSelector: TaskSelector,
		private readonly dxStorage: DxListsStorage,
		private readonly storage: ProjectStorage,
		@Inject(MAT_DIALOG_DATA) public readonly modalData: ModalData,
		@Inject(ActionIconsInjectable) private readonly actionIcons: AppIcons
	) {
		super();
		this.cmpId = 'task-list';
		this.dxLocalStorageKey = DcDxGrids.TaskListStandalone;

		this.vo$ = combineAll({
			isFirstLoad: this.firstLoadSubject,
			isLoading: this.taskSelector.isLoading$(),
			list: this.taskListSubject,
			viewActiveJobs: this.taskSelector.getViewActiveJobs$(),
			isJobsCacheActive: this.storage.isJobsCacheActive(),
			openedOverlays: this.openedOverlaySubject,
		});
	}

	public ngOnInit(): void {
		super.ngOnInit();
		this.triggerInit();
	}

	public toggleActiveJobs(value: MatSlideToggleChange): void {
		this.store.dispatch(
			updateViewActiveJobs({
				activeJobs: value.checked,
			})
		);
	}

	public toggleActiveCache(value: MatSlideToggleChange): void {
		this.store.dispatch(
			toggleJobsCacheActive({
				activeCache: value.checked,
			})
		);
	}

	public onKillAllTasksAction(): void {
		this.store.dispatch(killAllJobs());
	}

	public onClearCacheAction(): void {
		this.store.dispatch(clearCacheJobs());
	}

	public killTask(task: TaskItemEntity): void {
		this.store.dispatch(
			killJob({
				jobId: task.taskId,
			})
		);
	}

	public refresh(): void {
		this.firstLoadSubject.next(true);
		this.grid?.instance.beginCustomLoading('');
		this.store.dispatch(refreshTaskList());
	}

	public async onOpenErrorMessageAction(
		event: Event,
		taskId: number
	): Promise<void> {
		event.stopPropagation();
		const previous = await firstValueFrom(this.openedOverlaySubject);
		if (previous.errorMessageTaskId === taskId) {
			this.openedOverlaySubject.next({
				...previous,
				errorMessageTaskId: undefined,
			});
			return;
		}

		this.openedOverlaySubject.next({
			...previous,
			errorMessageTaskId: taskId,
		});
	}

	public closeOverlay(): void {
		this.openedOverlaySubject.next({
			errorMessageTaskId: undefined,
		});
	}

	public async onGridContentReady(): Promise<void> {
		const firstLoad = await firstValueFrom(this.firstLoadSubject);

		if (firstLoad) {
			await firstValueFrom(
				timer(1000).pipe(
					tap(() => {
						this.firstLoadSubject.next(false);
						this.grid?.instance.endCustomLoading();
					})
				)
			);
		}
	}

	public search(searchTerm: string): void {
		if (this.grid === null) {
			return;
		}

		this.grid.instance.filter([
			['taskType.value', DxOperations.Contains, searchTerm.trim()],
			'or',
			['projectLabel', DxOperations.Contains, searchTerm.trim()],
			'or',
			['elementLabel', DxOperations.Contains, searchTerm.trim()],
			'or',
			['elementType', DxOperations.Contains, searchTerm.trim()],
			'or',
			['status', DxOperations.Contains, searchTerm.trim()],
		]);
	}

	public calculateTaskTypeHeaderFilterExpression(
		valueExp: string,
		filterOperation: string,
		target: string
	): unknown {
		const column = this as any;
		const selector = (data: TaskItemEntity): boolean => {
			const entity = column.calculateCellValue(data) as {
				code: string;
				value: string;
			};
			if (entity === null && valueExp === NoopValue) {
				return true;
			}
			if (!entity) {
				return false;
			}
			const { value } = entity;
			return !!value && checkStringEquality(value, valueExp, filterOperation);
		};
		if (
			target === DxFilterElem.Header ||
			target === DxFilterElem.FilterRow ||
			target === DxFilterElem.FilterBuilder
		) {
			return [selector, DxOperations.Equal, true];
		}
		// eslint-disable-next-line prefer-rest-params
		return column.defaultCalculateFilterExpression.apply(this, arguments);
	}

	private triggerInit(): void {
		this.store.dispatch(initTaskList());

		of(null)
			.pipe(
				takeUntil(this.onDestroy$),
				tap(() => this.bootstrapIcons())
			)
			.subscribe();

		this.taskSelector
			.getTaskList$()
			.pipe(
				takeUntil(this.onDestroy$),
				debounceTime(320),
				tap((l) => {
					this.taskTypeHeaderFilter = l
						.extractTypes(NoopValue)
						.map(HeaderFilterDefaultMapper)
						.toArray();
					this.taskListSubject.next(l);
				})
			)
			.subscribe();
	}

	private async bootstrapIcons(): Promise<void> {
		const actions = [ActionIcons.ColumnExpositionChooser];
		const actionMap = await firstValueFrom(
			this.actionIcons.getFromRegistry(...actions)
		);
		this.panelActions = [
			{
				order: 0,
				command: (): void => {
					this.grid?.instance.showColumnChooser();
				},
				iconName: actionMap.get(ActionIcons.ColumnExpositionChooser)?.iconName,
				description: '',
				tooltip: $localize`:i18n=@@grid.list.columnSelection:`,
				typed: '',
			},
		];
	}
}
